import { useEffect, useState } from "react";
import {
  Alert, Avatar, Box, Drawer, Fab, IconButton, Paper, Stack, TextField, Tooltip,
  Typography, CircularProgress, Chip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import API, { getErrorMessage } from "../api/axios";
import { ACCENT, NAVY } from "../theme";

const suggestions = [
  "Find candidates with Java and AWS experience",
  "Summarize the strongest matches for my open positions",
  "What skills are most common in the candidate pool?",
];

export default function AiChatWidget() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(null);
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I can help you explore candidates and positions in the CRM. What are you looking for?" },
  ]);

  useEffect(() => {
    if (!open || configured !== null) return;
    API.get("/ai/status")
      .then(({ data }) => setConfigured(Boolean(data.enabled)))
      .catch(() => setConfigured(false));
  }, [open, configured]);

  const send = async (text = draft) => {
    const message = text.trim();
    if (!message || busy || configured === false) return;
    const history = messages.slice(-8).map(({ role, content }) => ({ role, content }));
    setMessages((current) => [...current, { role: "user", content: message }]);
    setDraft("");
    setError("");
    setBusy(true);
    try {
      const { data } = await API.post("/ai/chat", { message, history });
      setMessages((current) => [...current, { role: "assistant", content: data.answer || "I couldn't find an answer from the CRM records." }]);
    } catch (err) {
      setError(getErrorMessage(err, "The AI assistant couldn't respond. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title="AI recruitment assistant">
        <Fab
          color="primary"
          aria-label="Open AI recruitment assistant"
          onClick={() => setOpen(true)}
          sx={{ position: "fixed", right: 24, bottom: 88, zIndex: 1301, bgcolor: ACCENT }}
        >
          <AutoAwesomeIcon />
        </Fab>
      </Tooltip>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: { xs: "100vw", sm: 400 }, height: "100%", display: "flex", flexDirection: "column", bgcolor: "#f7f9fc" }}>
          <Box sx={{ px: 2, py: 1.7, bgcolor: NAVY, color: "white", display: "flex", alignItems: "center", gap: 1.25 }}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: ACCENT }}><SmartToyOutlinedIcon fontSize="small" /></Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>AI recruitment assistant</Typography>
              <Typography sx={{ color: "#bdc9dc", fontSize: 11 }}>Answers from your CRM records</Typography>
            </Box>
            <IconButton aria-label="Close chat" size="small" onClick={() => setOpen(false)} sx={{ color: "white" }}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Stack spacing={1.25} sx={{ p: 2, flex: 1, overflowY: "auto" }}>
            {configured === false && <Alert severity="info">The AI provider is not configured yet. Ask your CRM administrator to connect it before using chat.</Alert>}
            {configured === null && <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}><CircularProgress size={16} /><Typography sx={{ fontSize: 12 }}>Checking AI availability…</Typography></Box>}
            {messages.map((item, index) => (
              <Box key={`${item.role}-${index}`} sx={{ alignSelf: item.role === "user" ? "flex-end" : "flex-start", maxWidth: "90%" }}>
                <Paper elevation={0} sx={{ p: 1.35, bgcolor: item.role === "user" ? ACCENT : "white", color: item.role === "user" ? "white" : "text.primary", border: item.role === "user" ? 0 : "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.55 }}>{item.content}</Typography>
                </Paper>
              </Box>
            ))}
            {busy && <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}><CircularProgress size={16} /><Typography sx={{ fontSize: 12 }}>Searching CRM records…</Typography></Box>}
            {messages.length === 1 && configured && <Stack spacing={0.75} sx={{ mt: 1 }}>{suggestions.map((s) => <Chip key={s} label={s} onClick={() => send(s)} variant="outlined" sx={{ height: "auto", justifyContent: "flex-start", py: 0.7, whiteSpace: "normal", "& .MuiChip-label": { whiteSpace: "normal" } }} />)}</Stack>}
            {error && <Typography role="alert" color="error" sx={{ fontSize: 12 }}>{error}</Typography>}
          </Stack>
          <Box sx={{ p: 1.5, bgcolor: "white", borderTop: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField fullWidth size="small" multiline maxRows={4} placeholder={configured ? "Ask about candidates or positions…" : "AI assistant unavailable"} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} inputProps={{ maxLength: 1500, "aria-label": "Message AI assistant" }} disabled={configured !== true} />
              <IconButton color="primary" aria-label="Send message" disabled={busy || configured !== true || !draft.trim()} onClick={() => send()}><SendRoundedIcon /></IconButton>
            </Stack>
            <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: 10.5 }}>AI can make mistakes. Review candidate records before making decisions.</Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
